import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import { useCart } from '../../contexts/CartContext';
import { useMaintenance } from '../../contexts/MaintenanceContext';
import {
  db, collection, getDocs, query, where, doc, getDoc, setDoc, deleteDoc,
  onSnapshot, updateDoc, serverTimestamp,
} from '../../firebase/config';
import MaintenanceLifecycle, { computeLifecycle } from '../../components/MaintenanceLifecycle';
import MaintenanceModal from '../../components/MaintenanceModal';
import SharePaymentLinkModal from '../../components/SharePaymentLinkModal';
import ItemDetailModal from '../../components/ItemDetailModal';
import {
  Sparkles, Star, Plus, ArrowRight, Heart,
  MapPin, User, ShoppingBag,
  Scissors, Droplets, Eye, Flower2, TrendingUp,
  Trash2, Navigation, Calendar, Clock, CheckCircle2,
} from 'lucide-react';

const CATEGORY_META = {
  hair: { label: 'Hair', Icon: Scissors, fallbackImg: 'skytone-hair.jpeg' },
  nails: { label: 'Nails', Icon: Sparkles, fallbackImg: 'Skytone-nails.jpeg' },
  skin: { label: 'Skin', Icon: Droplets, fallbackImg: 'skytone-skin.jpeg' },
  lashes: { label: 'Lashes', Icon: Eye, fallbackImg: 'skytone-Lash.jpeg' },
  spa: { label: 'Spa', Icon: Flower2, fallbackImg: 'skytone-spa.jpeg' },
};

const haversineKm = (lat1, lng1, lat2, lng2) => {
  if ([lat1, lng1, lat2, lng2].some(v => v == null)) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

const NEAR_YOU_RADIUS_KM = 25;

function getServiceCover(service) {
  if (!service) return null;
  if (Array.isArray(service.media) && service.media.length > 0) {
    const first = service.media[0];
    if (first.type === 'image') return first.url;
  }
  if (service.media?.url) return service.media.url;
  if (service.mediaLegacy?.url) return service.mediaLegacy.url;
  return null;
}

const getServiceCategory = (service) => {
  const haystack = `${service.specialization || ''} ${service.category || ''} ${service.title || ''}`.toLowerCase();
  if (haystack.includes('nail') || haystack.includes('manicure') || haystack.includes('pedicure')) return 'nails';
  if (haystack.includes('lash') || haystack.includes('brow')) return 'lashes';
  if (haystack.includes('hair') || haystack.includes('stylist') || haystack.includes('cut') || haystack.includes('color')) return 'hair';
  if (haystack.includes('spa') || haystack.includes('massage')) return 'spa';
  if (haystack.includes('skin') || haystack.includes('facial') || haystack.includes('esthetician')) return 'skin';
  return null;
};

// ============ Booking helpers ============
const BOOKING_STATUS_META = {
  pending: { label: 'Pending', tone: 'amber', Icon: Clock },
  confirmed: { label: 'Confirmed', tone: 'green', Icon: CheckCircle2 },
  accepted: { label: 'Confirmed', tone: 'green', Icon: CheckCircle2 },
  completed: { label: 'Completed', tone: 'gray', Icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', tone: 'red', Icon: Clock },
  declined: { label: 'Declined', tone: 'red', Icon: Clock },
};

const getBookingTimestamp = (b) => {
  const candidate =
    b.scheduledAt || b.scheduledDate || b.date || b.startTime || b.createdAt;
  if (!candidate) return null;
  if (typeof candidate?.toDate === 'function') return candidate.toDate().getTime();
  if (typeof candidate?.toMillis === 'function') return candidate.toMillis();
  const parsed = new Date(candidate).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const formatBookingWhen = (b) => {
  const ts = getBookingTimestamp(b);
  if (!ts) return 'Date TBC';
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow =
    d.getFullYear() === tomorrow.getFullYear() &&
    d.getMonth() === tomorrow.getMonth() &&
    d.getDate() === tomorrow.getDate();

  const time = d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (sameDay) return `Today · ${time}`;
  if (isTomorrow) return `Tomorrow · ${time}`;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) + ` · ${time}`;
};

const HomePage = () => {
  const navigate = useNavigate();
  const { user, userData } = useAuth();
  const { currentLocation } = useLocation();
  const { addToCart } = useCart();
  const {
    schedules,
    loading: maintenanceLoading,
    deleteSchedule,
  } = useMaintenance();

  const [professionals, setProfessionals] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [categoryData, setCategoryData] = useState({});
  const [trendingService, setTrendingService] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [shareSchedule, setShareSchedule] = useState(null);

  const [previewItem, setPreviewItem] = useState(null);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [favLoaded, setFavLoaded] = useState(false);

  const handleProductClick = (product) => {
    setPreviewItem({ type: 'product', item: product });
  };

  const handleTrendingClick = () => {
    if (!trendingService) return;
    setPreviewItem({
      type: 'service',
      item: {
        ...trendingService,
        professionalName:
          trendingService.pro?.businessName ||
          trendingService.pro?.name ||
          trendingService.professionalName,
        professionalImage:
          trendingService.pro?.profileImage ||
          trendingService.pro?.coverImage ||
          trendingService.professionalImage,
        pro: trendingService.pro || null,
      },
    });
  };

  const closePreview = () => setPreviewItem(null);

  // ---------- Favorites ----------
  useEffect(() => {
    if (!user?.uid) {
      setFavoriteIds([]);
      setFavLoaded(false);
      return;
    }
    setFavLoaded(false);
    const favQ = query(collection(db, 'users', user.uid, 'favorites'));
    const unsub = onSnapshot(
      favQ,
      (snap) => {
        const ids = snap.docs
          .map((d) => d.data()?.productId || d.id)
          .filter(Boolean);
        setFavoriteIds(ids);
        setFavLoaded(true);
      },
      (err) => {
        console.warn('Favorites snapshot failed:', err?.message);
        setFavLoaded(true);
      }
    );
    return () => unsub();
  }, [user?.uid]);

  const handleToggleFavorite = async (itemId) => {
    if (!user?.uid) {
      alert('Sign in to save favorites.');
      return;
    }
    const isFav = favoriteIds.includes(itemId);

    setFavoriteIds((prev) =>
      isFav ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );

    try {
      const favRef = doc(db, 'users', user.uid, 'favorites', itemId);
      if (isFav) {
        await deleteDoc(favRef);
      } else {
        const product = products.find((p) => p.id === itemId);
        if (!product) {
          setFavoriteIds((prev) =>
            isFav ? [...prev, itemId] : prev.filter((id) => id !== itemId)
          );
          return;
        }

        const image =
          product.image ||
          (Array.isArray(product.media) &&
            product.media[0]?.type === 'image' &&
            product.media[0].url) ||
          product.mediaLegacy?.url ||
          null;

        await setDoc(favRef, {
          type: 'product',
          productId: product.id,
          name: product.name || 'Product',
          category: product.category || product.brand || 'Product',
          price: Number(product.price || 0),
          image,
          professionalId: product.professionalId || null,
          professionalName: product.professionalName || null,
          rating: null,
          addedAt: serverTimestamp(),
        });
      }
    } catch (err) {
      console.error('Toggle favorite failed:', err);
      setFavoriteIds((prev) =>
        isFav ? [...prev, itemId] : prev.filter((id) => id !== itemId)
      );
    }
  };

  // ---------- Mirror paid share-tokens ----------
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    const mirror = async () => {
      try {
        const q = query(
          collection(db, 'shareLinks'),
          where('ownerUid', '==', user.uid),
          where('type', '==', 'maintenance'),
          where('paid', '==', true),
          where('appliedToSchedule', '==', false)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        for (const docSnap of snap.docs) {
          const d = docSnap.data();
          if (!d.scheduleId) continue;
          try {
            await updateDoc(doc(db, 'users', user.uid, 'maintenance', d.scheduleId), {
              lastPaidAt: serverTimestamp(),
              lastPaidBy: d.paidBy?.name || 'Someone',
              updatedAt: serverTimestamp(),
            });
            await updateDoc(doc(db, 'shareLinks', docSnap.id), {
              appliedToSchedule: true,
            });
            console.log('[HomePage] Applied paid routine:', d.scheduleId);
          } catch (e) {
            console.warn('Mirror failed for', docSnap.id, e);
          }
        }
      } catch (err) {
        console.warn('mirrorPaidMaintenance:', err?.message);
      }
    };
    mirror();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // ---------- Load data ----------
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      try {
        const [prosSnap, productsSnap, servicesSnap] = await Promise.all([
          getDocs(query(collection(db, 'users'), where('role', '==', 'professional'))),
          getDocs(collection(db, 'products')),
          getDocs(collection(db, 'services')),
        ]);

        if (cancelled) return;

        const prosData = prosSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const productsData = productsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.active !== false);
        const servicesData = servicesSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => s.active !== false);

        setProfessionals(prosData);
        setProducts(productsData);
        setServices(servicesData);

        let bookingsData = [];
        if (user?.uid) {
          try {
            const bookingsSnap = await getDocs(collection(db, 'bookings'));
            bookingsData = bookingsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          } catch (err) {
            console.warn('Bookings failed to load (likely rules):', err.message);
          }
        }

        if (user?.uid && bookingsData.length > 0) {
          const uid = user.uid;
          const mine = bookingsData
            .filter(b =>
              b.clientId === uid ||
              b.userId === uid ||
              b.clientUid === uid ||
              b.ownerUid === uid ||
              b.customerId === uid ||
              b.customerUid === uid ||
              b.bookedBy === uid ||
              b.client?.id === uid ||
              b.client?.uid === uid
            )
            .map(b => {
              const pro = prosData.find(p => p.id === (b.professionalId || b.proId || b.pro?.id));
              const svc = servicesData.find(s => s.id === (b.serviceId || b.service || b.service?.id));
              return {
                ...b,
                _pro: pro || null,
                _svc: svc || null,
                _ts: getBookingTimestamp(b),
              };
            })
            .filter(b => b._ts != null)
            .sort((a, b) => (b._ts || 0) - (a._ts || 0))
            .slice(0, 5);
          setRecentBookings(mine);
        } else {
          setRecentBookings([]);
        }

        const catMap = {};
        Object.keys(CATEGORY_META).forEach(cat => {
          catMap[cat] = { count: 0, coverImage: null };
        });

        servicesData.forEach(s => {
          const cat = getServiceCategory(s);
          if (!cat) return;
          catMap[cat].count += 1;
          if (!catMap[cat].coverImage) {
            const cover = getServiceCover(s);
            if (cover) catMap[cat].coverImage = cover;
          }
        });

        setCategoryData(catMap);

        const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
        const recentBookingCounts = bookingsData.filter(b => {
          const t = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
          return t >= thirtyDaysAgo;
        });

        const serviceCounts = {};
        recentBookingCounts.forEach(b => {
          if (b.serviceId) {
            serviceCounts[b.serviceId] = (serviceCounts[b.serviceId] || 0) + 1;
          }
        });

        let topServiceId = null;
        let topCount = 0;
        Object.entries(serviceCounts).forEach(([id, count]) => {
          if (count > topCount) {
            topCount = count;
            topServiceId = id;
          }
        });

        let trending = null;
        if (topServiceId) {
          const svc = servicesData.find(s => s.id === topServiceId);
          if (svc) {
            const pro = prosData.find(p => p.id === svc.professionalId);
            trending = { ...svc, pro, bookingCount: topCount };
          }
        }
        if (!trending && servicesData.length > 0) {
          const withPro = servicesData.map(s => {
            const pro = prosData.find(p => p.id === s.professionalId);
            return { ...s, pro, bookingCount: 0 };
          });
          withPro.sort((a, b) => (Number(b.pro?.rating) || 0) - (Number(a.pro?.rating) || 0));
          trending = withPro[0];
        }
        setTrendingService(trending);

      } catch (err) {
        console.error('Error loading home data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const totalTasks = schedules.length;

  const freshTasks = useMemo(() => {
    return schedules.filter(s => {
      const { tone } = computeLifecycle(s);
      return tone === 'good';
    }).length;
  }, [schedules]);

  const completionPercentage = totalTasks > 0
    ? Math.round((freshTasks / totalTasks) * 100)
    : 0;

  const nearbyPros = useMemo(() => {
    if (!currentLocation?.lat) return professionals;
    return professionals.filter(p => {
      if (!p.coordinates?.lat) return false;
      const d = haversineKm(
        currentLocation.lat, currentLocation.lng,
        p.coordinates.lat, p.coordinates.lng
      );
      return d != null && d <= NEAR_YOU_RADIUS_KM;
    });
  }, [professionals, currentLocation]);

  const nearbyServices = useMemo(() => {
    if (!currentLocation?.lat || services.length === 0) return [];
    return services
      .map(s => {
        const pro = professionals.find(p => p.id === s.professionalId);
        if (!pro?.coordinates?.lat) return null;
        const d = haversineKm(
          currentLocation.lat, currentLocation.lng,
          pro.coordinates.lat, pro.coordinates.lng
        );
        if (d == null || d > NEAR_YOU_RADIUS_KM) return null;
        return { ...s, pro, distance: d };
      })
      .filter(Boolean)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 12);
  }, [services, professionals, currentLocation]);

  const categoryCards = useMemo(() => {
    return Object.entries(CATEGORY_META).map(([id, meta]) => {
      const data = categoryData[id] || { count: 0, coverImage: null };
      return {
        id,
        name: meta.label,
        count: data.count,
        img: data.coverImage || meta.fallbackImg,
      };
    });
  }, [categoryData]);

  const getProductImage = (product) => {
    if (product.image) return product.image;
    if (Array.isArray(product.media) && product.media.length > 0) {
      const first = product.media[0];
      if (first.type === 'image') return first.url;
    }
    if (product.mediaLegacy?.url) return product.mediaLegacy.url;
    return null;
  };

  const priceFrom = (service) => {
    if (Array.isArray(service.options) && service.options.length > 0) {
      return Math.min(...service.options.map(o => Number(o.price) || 0));
    }
    return Number(service.price) || 0;
  };

  const handleRebook = (schedule) => {
    if (schedule.preferredProId) {
      const url = `/professional/${schedule.preferredProId}${schedule.preferredServiceId ? `?service=${schedule.preferredServiceId}` : ''}`;
      navigate(url);
    } else {
      navigate(`/dashboard/discover/${schedule.category || 'all'}`);
    }
  };

  const handleFindNew = (schedule) => {
    navigate(`/dashboard/discover/${schedule.category || 'all'}`);
  };

  const handleDeleteRoutine = async (scheduleId, scheduleTitle) => {
    if (!window.confirm(`Remove "${scheduleTitle}"?`)) return;
    try {
      await deleteSchedule(scheduleId);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const openMaintenanceEdit = (schedule) => {
    setEditingSchedule(schedule);
    setShowMaintenanceModal(true);
  };

  const openMaintenanceAdd = () => {
    setEditingSchedule(null);
    setShowMaintenanceModal(true);
  };

  const goToService = (service) => {
    navigate(`/professional/${service.professionalId}?service=${service.id}`);
  };

  const goToCalendar = () => navigate('/dashboard/calendar');

  const DistanceChip = ({ distance, className = '' }) => {
    if (distance == null) return null;
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold text-gray-500 ${className}`}>
        <Navigation size={9} className="text-[#EFA9B6]" />
        {distance < 1 ? `${(distance * 1000).toFixed(0)} m` : `${distance.toFixed(1)} km`}
      </span>
    );
  };

  const BookingPill = ({ booking }) => {
    const meta = BOOKING_STATUS_META[booking.status] || BOOKING_STATUS_META.pending;
    const StatusIcon = meta.Icon;

    const title =
      booking._svc?.title ||
      booking.serviceName ||
      booking.title ||
      'Appointment';
    const proName =
      booking._pro?.businessName ||
      booking._pro?.name ||
      booking.professionalName ||
      booking.proName ||
      'Professional';

    const toneClasses = {
      amber: 'bg-amber-50 text-amber-700 border-amber-100',
      green: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      gray: 'bg-gray-50 text-gray-600 border-gray-100',
      red: 'bg-red-50 text-red-600 border-red-100',
    }[meta.tone];

    return (
      <button
        type="button"
        onClick={goToCalendar}
        className="shrink-0 inline-flex items-center gap-2.5 pl-2.5 pr-4 py-2 rounded-full bg-white border border-gray-100 shadow-sm hover:border-[#EFA9B6] hover:shadow-md transition-all text-left"
      >
        <div className="w-8 h-8 rounded-full bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6] shrink-0">
          <Calendar size={15} />
        </div>

        <div className="min-w-0">
          <p className="text-[12px] font-bold text-gray-900 leading-tight truncate max-w-[130px]">
            {title}
          </p>
          <p className="text-[10px] text-gray-500 leading-tight truncate max-w-[130px]">
            {proName} · {formatBookingWhen(booking)}
          </p>
        </div>

        <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full border ${toneClasses} shrink-0`}>
          <StatusIcon size={9} />
          {meta.label}
        </span>
      </button>
    );
  };

  if (loading || maintenanceLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // ============================================================
  // DESKTOP
  // ============================================================
  const DesktopHome = () => (
    <div className="hidden lg:block max-w-7xl mx-auto">
      <section className="relative overflow-hidden rounded-[40px] bg-[#fdf2ef] p-12 mb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 space-y-6">
            <span className="inline-block text-[#e67575] text-xs font-bold tracking-widest uppercase">
              WELCOME BACK
            </span>

            <h1 className="text-5xl sm:text-6xl lg:text-[76px] font-serif font-normal text-[#1e1917] leading-[1.1] tracking-tight">
              Hello, <br />
              <span className="text-[#e67575]">{userData?.name?.split(' ')[0] || 'Beautiful'}</span>
            </h1>

            <p className="text-[#685e59] text-base sm:text-lg max-w-md font-normal leading-relaxed mt-4">
              Your beauty journey continues. Track your maintenance, discover new services, and shop your favorites.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 pt-6">
              <button
                onClick={() => navigate('/dashboard/discover/all')}
                className="w-full sm:w-auto inline-flex items-center justify-center space-x-3 bg-[#1e1917] text-white px-8 py-4 rounded-full font-medium hover:bg-[#382f2c] transition-all group"
              >
                <span>Book Now</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate('/dashboard/search')}
                className="w-full sm:w-auto inline-flex items-center justify-center space-x-3 bg-white hover:bg-gray-50 text-[#1e1917] px-7 py-4 rounded-full font-medium transition-all shadow-sm border border-gray-100"
              >
                <span>Shop Products</span>
              </button>
            </div>

            <div className="flex flex-wrap justify-start gap-x-8 gap-y-4 pt-12">
              <div>
                <p className="text-2xl font-bold text-[#1e1917]">{totalTasks}</p>
                <p className="text-sm text-[#7a706b]">Active Tasks</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1e1917]">{nearbyPros.length}</p>
                <p className="text-sm text-[#7a706b]">Nearby Pros</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1e1917]">{completionPercentage}%</p>
                <p className="text-sm text-[#7a706b]">Routine Health</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 relative flex justify-center lg:justify-end h-full">
            {trendingService && getServiceCover(trendingService) ? (
              <img
                src={getServiceCover(trendingService)}
                alt={trendingService.title}
                className="w-full max-w-[800px] h-auto object-cover rounded-[40px] aspect-[4/3]"
              />
            ) : (
              <img
                src="skytone-header.jpeg"
                alt="Beauty"
                className="w-full max-w-[800px] h-auto object-cover rounded-[40px]"
              />
            )}
            {trendingService && (
              <button
                onClick={handleTrendingClick}
                className="absolute top-10 right-10 w-28 h-28 rounded-full bg-white shadow-xl flex flex-col items-center justify-center text-center p-2 hover:scale-105 transition-transform"
              >
                <TrendingUp size={18} className="text-[#e67575] mb-1" />
                <span className="font-serif font-bold text-[#1e1917] text-sm leading-tight">
                  Trending
                </span>
                <span className="text-[9px] text-[#7a706b] uppercase tracking-tight mt-0.5">
                  {trendingService.bookingCount > 0 ? `${trendingService.bookingCount} bookings` : 'Top Rated'}
                </span>
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl md:text-[40px] font-serif font-normal text-[#1e1917] flex items-center gap-3">
              Recent Bookings
              <Calendar size={28} className="text-[#e67575]" />
            </h2>
            <p className="text-sm text-[#7a706b] mt-2">
              Your upcoming and latest appointments
            </p>
          </div>
          <button
            onClick={goToCalendar}
            className="mt-6 sm:mt-0 px-6 py-2.5 rounded-full border border-gray-300 hover:border-[#1e1917] text-[#1e1917] text-sm font-medium transition-colors bg-white shadow-sm"
          >
            Open calendar
          </button>
        </div>

        {recentBookings.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {recentBookings.map(b => (
              <BookingPill key={b.id} booking={b} />
            ))}
          </div>
        ) : (
          <button
            onClick={goToCalendar}
            className="w-full py-4 rounded-2xl bg-white border border-dashed border-gray-200 text-sm text-gray-400 font-medium hover:border-[#EFA9B6] hover:text-[#EFA9B6] transition-colors"
          >
            No recent bookings — open calendar
          </button>
        )}
      </section>

      <section className="mb-12">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-[40px] font-serif font-normal text-[#1e1917]">
            Shop By Categories
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {categoryCards.map((cat) => (
            <button
              key={cat.id}
              onClick={() => navigate(`/dashboard/discover/${cat.id}`)}
              className="group cursor-pointer bg-white rounded-[32px] p-5 text-center hover:shadow-lg transition-all duration-300 flex flex-col items-center"
            >
              <div className="w-full aspect-square rounded-[24px] overflow-hidden bg-[#fdf2ef] mb-6 flex items-center justify-center p-4">
                <img
                  src={cat.img}
                  alt={cat.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 rounded-[16px]"
                  onError={(e) => {
                    const fallback = CATEGORY_META[cat.id]?.fallbackImg;
                    if (fallback && e.target.src !== fallback) {
                      e.target.src = fallback;
                    }
                  }}
                />
              </div>
              <h3 className="font-serif font-semibold text-[#1e1917] text-lg">{cat.name}</h3>
              <p className="text-sm text-[#7a706b] mt-1">
                {cat.count} {cat.count === 1 ? 'Service' : 'Services'}
              </p>
            </button>
          ))}
        </div>
      </section>

      {/* ============ NEAR YOU — DESKTOP: HORIZONTAL SCROLL ============ */}
      {nearbyServices.length > 0 && (
        <section className="mb-12">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl md:text-[40px] font-serif font-normal text-[#1e1917] flex items-center gap-3">
                Near You
                <Navigation size={28} className="text-[#e67575]" />
              </h2>
              <p className="text-sm text-[#7a706b] mt-2">
                Services from pros within {NEAR_YOU_RADIUS_KM}km of you
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard/search?near=1')}
              className="mt-6 sm:mt-0 px-6 py-2.5 rounded-full border border-gray-300 hover:border-[#1e1917] text-[#1e1917] text-sm font-medium transition-colors bg-white shadow-sm"
            >
              See all near me
            </button>
          </div>

          <div className="flex gap-6 overflow-x-auto hide-scrollbar pb-4 -mx-2 px-2 snap-x snap-mandatory">
            {nearbyServices.map(service => {
              const cover = getServiceCover(service);
              const pro = service.pro;
              return (
                <button
                  key={service.id}
                  onClick={() => goToService(service)}
                  className="shrink-0 w-[320px] snap-start group bg-white rounded-[32px] overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col border border-gray-100 text-left"
                >
                  <div className="w-full aspect-[4/3] bg-[#fdf2ef] overflow-hidden relative">
                    {cover ? (
                      <img
                        src={cover}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Sparkles size={40} className="text-[#e67575]" />
                      </div>
                    )}
                    <span className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-bold text-gray-700 shadow-sm inline-flex items-center gap-1">
                      <Navigation size={10} className="text-[#e67575]" />
                      {service.distance < 1
                        ? `${(service.distance * 1000).toFixed(0)} m`
                        : `${service.distance.toFixed(1)} km`}
                    </span>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    {service.category && (
                      <span className="text-[10px] font-bold tracking-wider text-[#e67575] uppercase mb-1">
                        {service.category}
                      </span>
                    )}
                    <h3 className="font-serif font-semibold text-[#1e1917] text-lg truncate">
                      {service.title}
                    </h3>
                    {pro && (
                      <p className="text-xs text-[#7a706b] mt-1 truncate">
                        {pro.businessName || pro.name}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
                      <span className="font-bold text-[#1e1917]">
                        {Array.isArray(service.options) && service.options.length > 0
                          ? `From R${priceFrom(service).toFixed(0)}`
                          : `R${priceFrom(service).toFixed(0)}`}
                      </span>
                      <span className="text-[#e67575] text-xs font-bold inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                        Book <ArrowRight size={12} />
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="mb-12">
        <div className="relative rounded-[40px] overflow-hidden bg-[#fdf2ef] p-10 md:p-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
            <div className="space-y-5">
              <span className="text-[#e67575] text-xs font-bold uppercase tracking-widest flex items-center">
                Glow Every Day <Sparkles size={16} className="ml-2" />
              </span>
              <h2 className="text-4xl md:text-[56px] font-serif font-normal text-[#1e1917] leading-tight">
                Discover <br />
                <span className="text-[#e67575]">Beautiful</span> Things
              </h2>
              <p className="text-[#685e59] text-base font-normal max-w-sm">
                {professionals.length} pros, {services.length} services, {products.length} products — all in one place.
              </p>
              <div className="pt-4">
                <button
                  onClick={() => navigate('/dashboard/search')}
                  className="inline-flex items-center space-x-3 bg-[#1e1917] text-white px-8 py-4 rounded-full font-medium hover:bg-[#382f2c] transition-colors group"
                >
                  <span>Shop Now</span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-end gap-12">
              <div className="space-y-10">
                <div className="text-center sm:text-left">
                  <p className="text-[#e67575] font-serif font-normal text-[40px] leading-none mb-1">
                    {professionals.length}+
                  </p>
                  <p className="text-sm text-[#7a706b] font-medium">Beauty Professionals</p>
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-[#e67575] font-serif font-normal text-[40px] leading-none mb-2">
                    {products.length}+
                  </p>
                  <p className="text-sm text-[#7a706b] font-medium">Products Available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-10">
          <h2 className="text-3xl md:text-[40px] font-serif font-normal text-[#1e1917]">
            Our Bestsellers
          </h2>
          <button
            onClick={() => navigate('/dashboard/search')}
            className="mt-6 sm:mt-0 px-6 py-2.5 rounded-full border border-gray-300 hover:border-[#1e1917] text-[#1e1917] text-sm font-medium transition-colors bg-white shadow-sm"
          >
            View All
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.slice(0, 4).map((product) => {
            const img = getProductImage(product);
            const isFav = favoriteIds.includes(product.id);
            return (
              <button
                key={product.id}
                onClick={() => handleProductClick(product)}
                className="group bg-white rounded-[32px] p-5 hover:shadow-xl transition-all duration-300 flex flex-col relative border border-gray-100 cursor-pointer text-left"
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleFavorite(product.id);
                  }}
                  className="absolute top-8 right-8 z-10 w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm hover:scale-110 transition-transform"
                >
                  <Heart
                    size={18}
                    className={isFav ? 'fill-[#EFA9B6] text-[#EFA9B6]' : 'text-gray-400'}
                  />
                </div>

                <div className="w-full aspect-[4/5] rounded-[24px] overflow-hidden bg-[#fdf2ef] mb-5 p-4 flex items-center justify-center">
                  {img ? (
                    <img
                      src={img}
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 rounded-[16px]"
                    />
                  ) : (
                    <ShoppingBag size={48} className="text-gray-300" />
                  )}
                </div>

                <h3 className="font-semibold text-[#1e1917] text-[15px] mb-2 truncate">
                  {product.name}
                </h3>

                <div className="flex items-center justify-between mt-auto">
                  <span className="font-bold text-lg text-[#1e1917]">
                    R{Number(product.price || 0).toFixed(2)}
                  </span>
                  <div className="w-10 h-10 rounded-full border border-gray-200 bg-white shadow-sm flex items-center justify-center">
                    <Plus size={16} className="text-[#1e1917]" />
                  </div>
                </div>
              </button>
            );
          })}
          {products.length === 0 && (
            <div className="col-span-4 text-center py-8 bg-white rounded-2xl border border-gray-50">
              <p className="text-gray-400">No products available yet</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );

  // ============================================================
  // MOBILE
  // ============================================================
  const MobileHome = () => (
    <div className="lg:hidden h-full overflow-y-auto pb-28 bg-[#FCF8F9] font-sans relative">
      <div className="absolute top-[-50px] right-[-50px] w-64 h-64 bg-[#EFA9B6]/20 rounded-full blur-3xl opacity-60"></div>
      <div className="absolute top-[200px] left-[-50px] w-48 h-48 bg-[#CDBCE7]/20 rounded-full blur-3xl opacity-50"></div>

      <div className="px-6 pt-6 pb-4 flex items-start justify-between relative z-10">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-[#FFF5F6] flex items-center justify-center border-2 border-[#FBE1E8] shrink-0 overflow-hidden">
            {userData?.profileImage ? (
              <img src={userData.profileImage} alt="" className="w-full h-full object-cover" />
            ) : (
              <User size={24} className="text-[#EFA9B6]" />
            )}
          </div>
          <div>
            <h1 className="font-serif font-bold text-2xl text-gray-900 leading-tight">
              Hey {userData?.name?.split(' ')[0] || 'Beautiful'}!
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {userData?.name?.split(' ').slice(1).join(' ') || ''}
            </p>
          </div>
        </div>
      </div>

      {trendingService && (
        <div className="px-6 mb-8 relative z-10">
          <button
            onClick={handleTrendingClick}
            className="relative rounded-[2rem] overflow-hidden shadow-[0_15px_40px_rgba(239,169,182,0.2)] group cursor-pointer h-[180px] w-full text-left"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10"></div>
            {getServiceCover(trendingService) ? (
              <img
                src={getServiceCover(trendingService)}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                alt={trendingService.title}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#EFA9B6] to-[#CDBCE7]"></div>
            )}
            <div className="absolute bottom-0 left-0 right-0 p-5 z-20">
              <div className="text-[10px] font-bold text-[#EFA9B6] uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                <Sparkles size={12} />
                {trendingService.bookingCount > 0 ? 'Trending Treatment' : 'Featured Service'}
              </div>
              <h3 className="text-white font-serif text-2xl mb-1 leading-tight drop-shadow-md">
                {trendingService.title}
              </h3>
              <p className="text-white/80 text-xs">
                {trendingService.pro?.businessName || trendingService.pro?.name || 'Professional'} · R{Number(trendingService.price || 0).toFixed(0)}
              </p>
            </div>
            <div className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white border border-white/30 shadow-sm">
              <ArrowRight size={16} className="-rotate-45" />
            </div>
          </button>
        </div>
      )}

      <div className="px-6 mb-8 relative z-10">
        <div className="grid grid-cols-5 gap-2">
          {Object.entries(CATEGORY_META).map(([id, meta]) => {
            const Icon = meta.Icon;
            const data = categoryData[id] || { count: 0 };
            return (
              <button
                key={id}
                onClick={() => navigate(`/dashboard/discover/${id}`)}
                className="flex flex-col items-center gap-2 py-3 rounded-2xl bg-white border border-gray-50 hover:border-[#EFA9B6] transition-colors group relative"
              >
                <div className="text-[#EFA9B6] group-hover:scale-110 transition-transform relative">
                  <Icon size={22} />
                  {data.count > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 bg-[#EFA9B6] text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                      {data.count > 9 ? '9+' : data.count}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                  {meta.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-6 relative z-10">
        <div className="px-6 flex justify-between items-end mb-3">
          <h2 className="text-sm font-bold text-gray-900 tracking-wide uppercase flex items-center gap-2">
            <Calendar size={14} className="text-[#EFA9B6]" />
            Recent Bookings
          </h2>
          <button
            onClick={goToCalendar}
            className="text-xs text-[#EFA9B6] font-bold"
          >
            See all
          </button>
        </div>

        {recentBookings.length > 0 ? (
          <div className="flex gap-2.5 overflow-x-auto hide-scrollbar px-6 pb-1">
            {recentBookings.map(b => (
              <BookingPill key={b.id} booking={b} />
            ))}
          </div>
        ) : (
          <div className="px-6">
            <button
              onClick={goToCalendar}
              className="w-full py-3 rounded-2xl bg-white border border-dashed border-gray-200 text-xs text-gray-400 font-medium hover:border-[#EFA9B6] hover:text-[#EFA9B6] transition-colors"
            >
              No recent bookings — open calendar
            </button>
          </div>
        )}
      </div>

      <div className="px-6 mb-8 relative z-10">
        <button
          type="button"
          onClick={openMaintenanceAdd}
          className="w-full bg-white rounded-3xl p-4 shadow-sm border border-gray-50 flex items-center gap-4 text-left hover:shadow-md transition-all"
        >
          <div className="w-12 h-12 rounded-full border-[3px] border-[#FFF5F6] flex items-center justify-center relative bg-white shrink-0">
            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 48 48">
              <circle cx="24" cy="24" r="20" fill="none" stroke="#FBE1E8" strokeWidth="3" />
              <circle
                cx="24" cy="24" r="20"
                fill="none"
                stroke="#EFA9B6"
                strokeWidth="3"
                strokeDasharray="125"
                strokeDashoffset={125 - (125 * completionPercentage) / 100}
                strokeLinecap="round"
              />
            </svg>
            <span className="relative font-bold text-gray-900 text-sm">
              {completionPercentage}%
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold text-gray-900 tracking-wide">Maintenance Tracker</h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {freshTasks} of {totalTasks} {totalTasks === 1 ? 'task' : 'tasks'} fresh this month
            </p>
          </div>

          <div className="w-8 h-8 rounded-full bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6] shrink-0">
            <Plus size={16} />
          </div>
        </button>

        {schedules.length > 0 && (
          <div className="space-y-4 mt-4">
            {schedules.map((schedule) => {
              const lifecycle = computeLifecycle(schedule);
              const overdue = lifecycle.tone === 'overdue';
              const scheduleTitle = schedule.title || `${schedule.category} routine`;

              return (
                <div
                  key={schedule.id}
                  className="bg-white rounded-3xl p-5 shadow-sm border border-gray-50 hover:shadow-md transition-all"
                >
                  <div className="flex justify-between items-start mb-3 gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-gray-900 text-[15px] mb-0.5 flex items-center gap-2">
                        <button
                          onClick={() => openMaintenanceEdit(schedule)}
                          className="truncate text-left hover:underline"
                        >
                          {scheduleTitle}
                        </button>
                        {overdue && (
                          <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse shrink-0"></span>
                        )}
                      </h3>
                      <p className="text-[11px] text-gray-500 font-medium truncate">
                        {schedule.preferredProName || 'Any pro'}
                      </p>
                      {schedule.lastPaidBy && (
                        <p className="text-[10px] text-green-600 font-bold mt-0.5">
                          Paid by {schedule.lastPaidBy}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-bold text-gray-900">
                        R{Number(schedule.price || 0).toFixed(0)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDeleteRoutine(schedule.id, scheduleTitle)}
                        className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                        title="Remove routine"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-1 mb-3">
                    <MaintenanceLifecycle schedule={schedule} />
                  </div>

                  {lifecycle.tone !== 'good' && (
                    <div className="flex gap-2 pt-3 border-t border-gray-50">
                      <button
                        onClick={() => handleRebook(schedule)}
                        className="flex-1 py-2 rounded-xl bg-[#FFF5F6] text-[#EFA9B6] text-xs font-bold hover:bg-[#FBE1E8] transition-colors"
                      >
                        Rebook
                      </button>
                      <button
                        onClick={() => handleFindNew(schedule)}
                        className="flex-1 py-2 rounded-xl bg-gray-50 text-gray-600 text-xs font-bold hover:bg-gray-100 transition-colors"
                      >
                        Find New
                      </button>
                    </div>
                  )}

                  <button
                    onClick={() => setShareSchedule(schedule)}
                    className="w-full mt-2 py-2 rounded-xl bg-white border border-[#FFF5F6] text-[#EFA9B6] text-xs font-bold hover:bg-[#FFF5F6] transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Sparkles size={12} />
                    Ask someone to pay
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {nearbyServices.length > 0 && (
        <div className="mb-8 relative z-10">
          <div className="px-6 flex justify-between items-end mb-4">
            <h2 className="text-sm font-bold text-gray-900 tracking-wide uppercase flex items-center gap-2">
              <Navigation size={14} className="text-[#EFA9B6]" />
              Near You
            </h2>
            <button
              onClick={() => navigate('/dashboard/search?near=1')}
              className="text-xs text-[#EFA9B6] font-bold"
            >
              See all
            </button>
          </div>

          <div className="flex gap-3 overflow-x-auto hide-scrollbar px-6 pb-2">
            {nearbyServices.map(service => {
              const cover = getServiceCover(service);
              return (
                <button
                  key={service.id}
                  onClick={() => goToService(service)}
                  className="shrink-0 w-[220px] bg-white rounded-3xl overflow-hidden border border-gray-50 shadow-sm hover:shadow-md transition-all text-left"
                >
                  <div className="w-full h-[130px] bg-[#FFF5F6] relative overflow-hidden">
                    {cover ? (
                      <img
                        src={cover}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Sparkles size={32} className="text-[#EFA9B6]" />
                      </div>
                    )}
                    <span className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-2 py-0.5 rounded-full text-[9px] font-bold text-gray-700 shadow-sm inline-flex items-center gap-1">
                      <Navigation size={9} className="text-[#EFA9B6]" />
                      {service.distance < 1
                        ? `${(service.distance * 1000).toFixed(0)}m`
                        : `${service.distance.toFixed(1)}km`}
                    </span>
                  </div>
                  <div className="p-3">
                    <h3 className="font-bold text-gray-900 text-sm truncate">
                      {service.title}
                    </h3>
                    {service.pro && (
                      <p className="text-[10px] text-gray-500 mt-0.5 truncate">
                        {service.pro.businessName || service.pro.name}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm font-bold text-gray-900">
                        R{priceFrom(service).toFixed(0)}
                      </span>
                      <span className="text-[10px] font-bold text-[#EFA9B6]">
                        Book
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // ============================================================
  // ROOT RENDER
  // ============================================================
  return (
    <>
      <DesktopHome />
      <MobileHome />

      {showMaintenanceModal && (
        <MaintenanceModal
          schedule={editingSchedule}
          onClose={() => {
            setShowMaintenanceModal(false);
            setEditingSchedule(null);
          }}
        />
      )}

      {shareSchedule && (
        <SharePaymentLinkModal
          schedule={shareSchedule}
          onClose={() => setShareSchedule(null)}
        />
      )}

      {previewItem && (
        <ItemDetailModal
          type={previewItem.type}
          item={previewItem.item}
          onClose={closePreview}
          onAddToCart={(product, qty) => {
            addToCart(product, qty);
          }}
          onBook={(service) => {
            closePreview();
            navigate(`/professional/${service.professionalId}?service=${service.id}`);
          }}
          onToggleFavorite={handleToggleFavorite}
          isFavorite={favoriteIds.includes(previewItem.item.id)}
        />
      )}

      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </>
  );
};

export default HomePage;